import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL;

function authConfig(extra = {}) {
  const token = window.localStorage.getItem('token');
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
}

export function hasCommunicationMedia(payload = {}) {
  return Boolean(payload?.media_file || payload?.mediaFile || payload?.media?.file);
}

export function buildCommunicationMessageFormData(payload = {}) {
  const formData = new FormData();
  const mediaFile = payload.media_file || payload.mediaFile || payload.media?.file;
  const mediaType = payload.media_type || payload.mediaType || payload.media?.type;
  const skipKeys = new Set(['media_file', 'mediaFile', 'media_type', 'mediaType', 'media']);

  Object.entries(payload).forEach(([key, value]) => {
    if (skipKeys.has(key) || value === undefined || value === null || value === '') return;
    formData.append(key, value);
  });

  if (mediaType && !formData.has('message_type')) {
    formData.append('message_type', mediaType);
  }
  if (mediaFile) {
    formData.append('media', mediaFile, mediaFile.name || `fastmax-${mediaType || 'media'}`);
  }

  return formData;
}

export async function createOrGetDeliveryConversation(deliveryPointId, payload = {}) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/delivery/${deliveryPointId}/conversation`,
    payload,
    authConfig()
  );
  return data.conversation;
}

export async function createOrGetSupportConversation(scope, userId) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/support/${scope}/${userId}/conversation`,
    {},
    authConfig()
  );
  return data.conversation;
}

export async function fetchSupportUnreadCount(scope, userId) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/support/${scope}/${userId}/unread-count`,
    authConfig()
  );
  return data;
}

export async function createOrGetFinalClientDeliveryConversation(deliveryPointId) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/final-client/delivery/${deliveryPointId}/conversation`
  );
  return data;
}

export async function fetchConversationMessages(conversationId, viewer = {}) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/conversations/${conversationId}/messages`,
    authConfig({
      params: {
        viewer_type: viewer.viewer_type || viewer.currentUserType,
        user_id: viewer.user_id || viewer.currentUserId,
      },
    })
  );
  return data;
}

export async function sendConversationMessage(conversationId, payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/messages`,
    payload,
    authConfig()
  );
  return data.message;
}

export async function sendDeliveryCustomerWhatsApp(deliveryPointId, payload) {
  if (payload instanceof FormData) {
    const { data } = await axios.post(
      `${API_URL}/api/communications/delivery/${deliveryPointId}/customer-whatsapp`,
      payload,
      authConfig()
    );
    return data;
  }

  const { data } = await axios.post(
    `${API_URL}/api/communications/delivery/${deliveryPointId}/customer-whatsapp`,
    payload,
    authConfig()
  );
  return data;
}

export async function fetchDriverCustomerChannelStatus(conversationId) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/conversations/${conversationId}/driver-customer/status`,
    authConfig()
  );
  return data;
}

export async function sendDriverCustomerMessage(conversationId, payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/driver-customer/messages`,
    payload,
    authConfig()
  );
  return data;
}

export async function fetchConversationDeliverySlotAvailability(conversationId) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/conversations/${conversationId}/delivery-slot-availability`,
    authConfig()
  );
  return data;
}

export async function updateConversationDeliverySlot(conversationId, slot) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/delivery-slot`,
    { slot },
    authConfig()
  );
  return data;
}

export async function fetchConversationWhatsAppTemplates(conversationId) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/conversations/${conversationId}/whatsapp/templates`,
    authConfig()
  );
  return data;
}

export async function sendConversationWhatsAppMessage(conversationId, payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/whatsapp/messages`,
    payload,
    authConfig()
  );
  return data;
}

export async function sendDirectSupportWhatsAppMessage(conversationId, payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/direct-whatsapp/messages`,
    payload,
    authConfig()
  );
  return data;
}

export async function sendConversationWhatsAppTemplate(conversationId, templateId) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/whatsapp/templates`,
    { template: templateId },
    authConfig()
  );
  return data;
}

export async function fetchConversationAIState(conversationId) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/conversations/${conversationId}/ai`,
    authConfig()
  );
  return data;
}

export async function fetchConversationWorkspace(conversationId) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/conversations/${conversationId}/workspace`,
    authConfig()
  );
  return data;
}

export async function fetchConversationIntelligence(conversationId) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/conversations/${conversationId}/intelligence`,
    authConfig()
  );
  return data;
}

export async function refreshConversationIntelligence(conversationId) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/intelligence/refresh`,
    {},
    authConfig()
  );
  return data;
}

export async function previewConversationAction(conversationId, actionType, payload = {}) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/actions/${actionType}/preview`,
    payload,
    authConfig()
  );
  return data;
}

export async function executeConversationAction(conversationId, actionType, payload = {}) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/actions/${actionType}/execute`,
    payload,
    authConfig()
  );
  return data;
}

export async function fetchDriverRouteContext(conversationId) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/conversations/${conversationId}/driver-route-context`,
    authConfig()
  );
  return data;
}

export async function updateConversationAISettings(conversationId, payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/ai-settings`,
    payload,
    authConfig()
  );
  return data;
}

export async function generateConversationAIDraft(conversationId, payload = {}) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/ai-draft`,
    payload,
    authConfig()
  );
  return data;
}

export async function improveConversationAIMessage(conversationId, content, payload = {}) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/ai-improve-message`,
    { ...payload, content },
    authConfig()
  );
  return data;
}

export async function acceptConversationAIDraft(conversationId, draftId, content) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/ai-drafts/${draftId}/accept`,
    { content },
    authConfig()
  );
  return data;
}

export async function dismissConversationAIDraft(conversationId, draftId) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/ai-drafts/${draftId}/dismiss`,
    {},
    authConfig()
  );
  return data;
}

export async function requireConversationHandoff(conversationId, reason) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/handoff`,
    { reason },
    authConfig()
  );
  return data;
}

export async function resolveConversationIdentity(conversationId, payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/identity/resolve`,
    payload,
    authConfig()
  );
  return data;
}

export async function linkConversationDelivery(conversationId, deliveryPointId) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/link-delivery`,
    { delivery_point_id: deliveryPointId },
    authConfig()
  );
  return data;
}

export async function markConversationRead(conversationId, payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/conversations/${conversationId}/read`,
    payload,
    authConfig()
  );
  return data;
}

export async function fetchDriverUnreadCount(userId, params = { include_support: 0 }) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/driver/${userId}/unread-count`,
    authConfig({ params })
  );
  return data;
}

export async function fetchAdminTriageSummary(params = {}) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/admin/conversations/triage-summary`,
    authConfig({ params })
  );
  return data;
}

export async function fetchProspectLeads(params = {}) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/admin/prospects`,
    authConfig({ params })
  );
  return data;
}

export async function fetchProspectSummary(params = {}) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/admin/prospects/summary`,
    authConfig({ params })
  );
  return data;
}

export async function fetchProspectAISettings() {
  const { data } = await axios.get(
    `${API_URL}/api/communications/admin/prospects/ai-settings`,
    authConfig()
  );
  return data;
}

export async function fetchProspectLeadWorkspace(leadId, params = {}) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/admin/prospects/${leadId}/workspace`,
    authConfig({ params })
  );
  return data;
}

export async function fetchProspectKnowledge() {
  const { data } = await axios.get(
    `${API_URL}/api/communications/admin/prospects/knowledge`,
    authConfig()
  );
  return data;
}

export async function createProspectKnowledge(payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/admin/prospects/knowledge`,
    payload,
    authConfig()
  );
  return data;
}

export async function updateProspectKnowledge(entryId, payload) {
  const { data } = await axios.patch(
    `${API_URL}/api/communications/admin/prospects/knowledge/${entryId}`,
    payload,
    authConfig()
  );
  return data;
}

export async function updateProspectAISettings(payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/admin/prospects/ai-settings`,
    payload,
    authConfig()
  );
  return data;
}

export async function updateProspectLeadStage(leadId, payload) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/admin/prospects/${leadId}/stage`,
    payload,
    authConfig()
  );
  return data;
}

export async function updateProspectLeadAIMode(leadId, aiAutoEnabled) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/admin/prospects/${leadId}/ai-mode`,
    { ai_auto_enabled: aiAutoEnabled },
    authConfig()
  );
  return data;
}

export async function sendProspectRegistrationLink(leadId) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/admin/prospects/${leadId}/send-registration-link`,
    {},
    authConfig()
  );
  return data;
}

export async function markProspectConverted(leadId, payload = {}) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/admin/prospects/${leadId}/mark-converted`,
    payload,
    authConfig()
  );
  return data;
}

export async function triggerProspectAutoReply(leadId) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/admin/prospects/${leadId}/auto-reply`,
    {},
    authConfig()
  );
  return data;
}

export async function fetchAdminDriverTimeline(params = {}) {
  const { data } = await axios.get(
    `${API_URL}/api/communications/admin/drivers/timeline`,
    authConfig({ params })
  );
  return data;
}

export async function recordDriverTimelineGuidance(payload = {}) {
  const { data } = await axios.post(
    `${API_URL}/api/communications/admin/drivers/timeline/guidance`,
    payload,
    authConfig()
  );
  return data;
}

export async function updateDeliveryAlert(alertId, action) {
  const { data } = await axios.patch(
    `${API_URL}/api/delivery-alerts/${alertId}`,
    { action },
    authConfig()
  );
  return data.alert;
}

export async function fetchConversations(scope, idOrParams, params = {}) {
  let url;
  let query = params;

  if (scope === 'admin') {
    url = `${API_URL}/api/communications/admin/conversations`;
    query = idOrParams || {};
  } else {
    url = `${API_URL}/api/communications/${scope}/${idOrParams}/conversations`;
  }

  const { data } = await axios.get(url, authConfig({ params: query }));
  return data;
}
